import * as React from 'react';
import { useState, useEffect } from 'react';

import { SnowSearchTopView, SnowSearchHeaderView,
  SnowSearchTopMobileView, SnowSearchHeaderMobileView } from './views/SnowSearchContainerView';
import FieldGroupView from './views/FieldGroupView';
import FieldKeyword from './FieldKeyword';
import FieldDatePicker from './FieldDatePicker';
import FieldRooms from './FieldRooms';
import YadoSubmitButton from './YadoSubmitButton';
import { SNOW_SEARCH_FIELD_FOCUSED, SNOW_SEARCH_FIELD_BLURRED,
  SNOW_SEARCH_SUGGESTION_SELECTED, SNOW_SEARCH_DATE_SELECTED } from './events';
import { getLocale } from '../utils/pathHelper';
import { ISnowSearchPlacement, ISearchFormProps, IFieldType, ISuggestionList, ISuggestion, IRoom } from './types';
import { defaultRooms, defaultSuggestion, defaultSuggestionList,
  searchYadoByName, searchYadoFromResort, searchYadoFromPrefecture, searchYadoFromArea } from './helpers';
import { createDateList } from '../utils/dateHelper';
import t from './locales';

const ELEMENT_ID = 'snow-search-yado-search-form';

interface IProps extends ISearchFormProps {
  recommended_stay_list?: ISuggestionList;
  placement: ISnowSearchPlacement;
  isMobile: boolean;
  is_toppage: boolean;
}

const YadoSnowSearch: React.FC<IProps> = (props) => {
  const { q, isMobile, placement, is_toppage } = props;

  const [focusedField, setFocusedField] = useState<IFieldType>('');
  const [keyword, setKeyword] = useState<string>(q && q.label || '');
  const [suggestionLabelHidden, hideSuggestionLabel] = useState(false);
  const [suggestionList, setSuggestionList] = useState<ISuggestionList>(props.recommended_stay_list || defaultSuggestionList);
  const [selectedSuggestion, setSuggestion] = useState<ISuggestion>(defaultSuggestion(q));
  const [selectedDateList, setDateList] = useState<Date[]>(createDateList(props.checkin_date, props.checkout_date));
  const [selectedRooms, setRooms] = useState<IRoom[]>(defaultRooms(props.rooms));
  const yadoCandidateList = props.yado_candidate_list;
  const locale = getLocale({ underscore: true });

  function handleKeywordChange(event: React.ChangeEvent) {
    const value = (event.target as HTMLInputElement).value;
    const searchByKeyword = (word: string) => ({
      resort: searchYadoFromResort(word, locale, yadoCandidateList.resort, yadoCandidateList.prefecture, yadoCandidateList.area),
      prefecture: searchYadoFromPrefecture(word, locale, yadoCandidateList.prefecture),
      area: searchYadoFromArea(word, locale, yadoCandidateList.area),
      yado: searchYadoByName(word, locale, yadoCandidateList.yado, yadoCandidateList.resort, yadoCandidateList.prefecture),
    });
    setSuggestionList(searchByKeyword(value));
    setKeyword(value);
  }

  // 背景クリックでバックドロップを非表示にする
  const bodyElement: HTMLElement = document.getElementsByTagName('body')[0];

  useEffect(() => {
    const handleFocus = (event: CustomEvent) => {
      if (event.detail.placement !== placement) { return; }
      setFocusedField(event.detail.focusedField);
    }
    const handleBlur = () => {
      setFocusedField('');
    }
    const handleSuggestionSelected = (event: CustomEvent) => {
      if (event.detail.placement !== placement) { return; }
      const { suggestion } = event.detail;
      setSuggestion(suggestion);
      hideSuggestionLabel(false);
      setFocusedField('date');
    }
    const handleDateSelected = (event: CustomEvent) => {
      if (event.detail.placement !== placement) { return; }
      setFocusedField('rooms');
      if (event.detail.isRoomsEmpty) { setRooms(defaultRooms()); }
    }
    const handleBlurAreaClick = (event: MouseEvent) => {
      const clickedClassName = event.target && (event.target as HTMLElement).getAttribute('class');
      const hideBackdrop = [
        'home-hero',
        'home-hero__top',
        'home-hero__bottom',
        'home-hero-banner',
        'home-search-container',
        'home-bg',
      ].indexOf(clickedClassName) !== -1;
      if (hideBackdrop) {
        document.dispatchEvent(new CustomEvent(SNOW_SEARCH_FIELD_BLURRED));
      }
    }
    document.addEventListener(SNOW_SEARCH_FIELD_FOCUSED, handleFocus);
    document.addEventListener(SNOW_SEARCH_FIELD_BLURRED, handleBlur);
    document.addEventListener(SNOW_SEARCH_SUGGESTION_SELECTED, handleSuggestionSelected);
    document.addEventListener(SNOW_SEARCH_DATE_SELECTED, handleDateSelected);
    bodyElement.addEventListener('click', handleBlurAreaClick);
    return () => {
      document.removeEventListener(SNOW_SEARCH_FIELD_FOCUSED, handleFocus);
      document.removeEventListener(SNOW_SEARCH_FIELD_BLURRED, handleBlur);
      document.removeEventListener(SNOW_SEARCH_SUGGESTION_SELECTED, handleSuggestionSelected);
      document.removeEventListener(SNOW_SEARCH_DATE_SELECTED, handleDateSelected);
      bodyElement.removeEventListener('click', handleBlurAreaClick);
    }
  }, []);

  useEffect(() => {
    if (focusedField === 'keyword') {
      setSuggestion(null);
      hideSuggestionLabel(true);
    }
    if (focusedField === 'rooms' && selectedRooms.length === 0) {
      setRooms(defaultRooms())
    }
  }, [focusedField]);

  let Container = SnowSearchTopView;
  if (!isMobile) {
    Container = ['top', 'modal'].includes(placement) ? SnowSearchTopView : SnowSearchHeaderView;
  } else {
    Container = ['top', 'modal'].includes(placement) ? SnowSearchTopMobileView : SnowSearchHeaderMobileView;
  }

  if (is_toppage) {
    return(
      <Container id={ELEMENT_ID}>
        <div className='wrapper'>
          <FieldKeyword
            searchContext="stay"
            placement={placement}
            placeholder={t.yadoSearchPlaceholder()}
            isDropDownShown={focusedField === 'keyword'}
            selectedSuggestion={selectedSuggestion}
            suggestionList={suggestionList}
            labelHidden={suggestionLabelHidden}
            handleChange={handleKeywordChange} />
          <FieldDatePicker
            placement={placement}
            isDropDownShown={focusedField === 'date'}
            selectedDateList={selectedDateList}
            setDateList={setDateList}
            numOfCalendar={isMobile ? 1 : 2}
            rooms={selectedRooms} />
          <FieldGroupView>
            <FieldRooms
              placement={placement}
              isDropDownShown={focusedField === 'rooms'}
              rooms={selectedRooms}
              setRooms={setRooms} />
            <YadoSubmitButton
              label={t.search()}
              keyword={keyword}
              selectedSuggestion={selectedSuggestion}
              selectedDateList={selectedDateList}
              selectedRooms={selectedRooms}
            />
          </FieldGroupView>
        </div>
      </Container>
    )
  } else {
    return(
      <Container id={ELEMENT_ID}>
        <FieldKeyword
          searchContext="stay"
          placement={placement}
          placeholder={t.yadoSearchPlaceholder()}
          isDropDownShown={focusedField === 'keyword'}
          selectedSuggestion={selectedSuggestion}
          suggestionList={suggestionList}
          labelHidden={suggestionLabelHidden}
          handleChange={handleKeywordChange} />
        <FieldDatePicker
          placement={placement}
          isDropDownShown={focusedField === 'date'}
          selectedDateList={selectedDateList}
          setDateList={setDateList}
          numOfCalendar={isMobile ? 1 : 2}
          rooms={selectedRooms} />
        <FieldGroupView>
          <FieldRooms
            placement={placement}
            isDropDownShown={focusedField === 'rooms'}
            rooms={selectedRooms}
            setRooms={setRooms} />
          <YadoSubmitButton
            label={t.search()}
            keyword={keyword}
            selectedSuggestion={selectedSuggestion}
            selectedDateList={selectedDateList}
            selectedRooms={selectedRooms}
          />
        </FieldGroupView>
      </Container>
    )
  }
};

export default YadoSnowSearch;
